import Play from "@assets/icons/play.svg?react";
import Link from "@components/Link";
import OptimisedImage from "@components/OptimisedImage/OptimisedImage";
import { BlockTag } from "@components/SectionTag";
import type { BFFArticleCard } from "@src/app/types/bff";
import type { Section } from "@src/app/types/Data";
import clsx from "clsx";
import type { ReactElement } from "react";

interface HeroCardProps {
  article: BFFArticleCard;
  // used for homepage carousel
  translucent?: boolean;
}

export default function HeroCard({
  article,
  translucent = true,
}: HeroCardProps): ReactElement {
  return (
    <Link
      to={`${article.urlPath}?ref=home-hero-carousel`}
      className="block size-full"
    >
      <section
        data-testid={`herocard-${article.id}`}
        className="group relative size-full cursor-pointer overflow-hidden"
        style={{ boxShadow: "3px 3px 5px 0px rgba(0, 0, 0, 0.07)" }}
      >
        {article.mediaType === "video" && (
          <div className="absolute left-1/2 z-20 flex size-[60px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-icon-opacity-60 max-lg:top-[calc((100vw-24px)/3)] lg:top-1/2">
            <Play className="size-[28px]" />
          </div>
        )}

        <div className="relative w-full lg:absolute lg:z-10">
          <OptimisedImage
            rootClassName={clsx(
              "aspect-[3/2] transition-transform duration-300 ",
              { "lg:group-hover:scale-105": translucent },
            )}
            src={article.cover}
            suffix={
              <div className="absolute bottom-0 left-0 flex lg:hidden">
                <BlockTag
                  section={article.section as Exclude<Section, "home">}
                />
              </div>
            }
            {...{ fetchpriority: "high" }}
          />
        </div>

        <section className="flex min-h-[146px] w-full flex-col lg:absolute lg:bottom-0 lg:z-[11]">
          <div className="hidden h-[28px] w-full lg:flex">
            <BlockTag section={article.section as Exclude<Section, "home">} />
          </div>
          <h1 className="box-border flex-1 p-[16px] text-primary max-lg:bg-white lg:bg-[linear-gradient(6deg,_rgba(0,_0,_0,_0.70)_32.45%,_rgba(0,_0,_0,_0.35)_78.03%,_rgba(0,_0,_0,_0.00)_99.24%)] lg:p-[25.2px] lg:text-primary-on-dark">
            <p
              className={clsx(
                "line-clamp-3 text-header-3 lg:line-clamp-2 lg:text-header-1",
                { "lg:group-hover:underline": translucent },
              )}
            >
              {article.title}
            </p>

            <p className="mt-[8px] line-clamp-1 text-subheader text-tertiary">
              {article.description}
            </p>
          </h1>
        </section>
      </section>
    </Link>
  );
}
